/**
 * Function Node Management → Skills tab.
 * Binds Skill Library skills to a capability (canonical SoT remains Skill 库).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Button,
  Empty,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import {
  platformBindNodeSkill,
  platformClearNodeSkillDefault,
  platformDisableNodeSkillBinding,
  platformEnableNodeSkillBinding,
  platformListNodeSkillBindings,
  platformListSkills,
  platformReorderNodeSkillBindings,
  platformSetNodeSkillDefault,
  platformUnbindNodeSkill,
  type NodeSkillBindingRow,
} from '../api'
import { SectionCard } from './adminShared'
import { platformColors } from './theme'

type Props = {
  capability: string | null
  canvasNodeType?: string | null
}

export default function NodeSkillsPanel({ capability, canvasNodeType }: Props) {
  const { t } = useTranslation('platformAdmin')
  const [loading, setLoading] = useState(false)
  const [bindings, setBindings] = useState<NodeSkillBindingRow[]>([])
  const [stats, setStats] = useState<{
    bound_count: number
    published_count: number
    enabled_count: number
    default_skill_name: string | null
    calls_30d: number | null
    avg_latency_ms: number | null
  } | null>(null)
  const [bindOpen, setBindOpen] = useState(false)
  const [skillOptions, setSkillOptions] = useState<Array<{ value: string; label: string }>>([])
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null)
  const [binding, setBinding] = useState(false)

  const reload = useCallback(async () => {
    if (!capability) {
      setBindings([])
      setStats(null)
      return
    }
    setLoading(true)
    try {
      const res = await platformListNodeSkillBindings(capability)
      setBindings(res.data.bindings || [])
      setStats(res.data.stats || null)
    } catch (e: any) {
      message.error(e?.response?.data?.detail?.message || e?.message || 'Failed to load skills')
    } finally {
      setLoading(false)
    }
  }, [capability])

  useEffect(() => {
    void reload()
  }, [reload])

  const openBind = async () => {
    if (!capability) return
    setBindOpen(true)
    setSelectedSkillId(null)
    try {
      // VISTOERA_SKILL_PACKAGE_REGISTRY_..._P0_V1 §6 — the picker must only
      // ever offer Skills that declare compatibility with this Function
      // Node's capability (never the full Skill Library / an arbitrary
      // list), so binding can never create an incompatible pairing.
      const res = await platformListSkills({ capability })
      const items = (res.data.items || []) as Array<{
        id: string
        display_name: string
        skill_key: string
      }>
      const bound = new Set(bindings.map((b) => b.skill_id))
      setSkillOptions(
        items
          .filter((s) => !bound.has(s.id))
          .map((s) => ({ value: s.id, label: `${s.display_name} (${s.skill_key})` })),
      )
    } catch {
      setSkillOptions([])
    }
  }

  const onBind = async () => {
    if (!capability || !selectedSkillId) return
    setBinding(true)
    try {
      await platformBindNodeSkill(capability, {
        skill_id: selectedSkillId,
        canvas_node_type: canvasNodeType || undefined,
        enabled: true,
        is_default: bindings.length === 0,
      })
      message.success(t('providers.skills.bindSuccess'))
      setBindOpen(false)
      await reload()
    } catch (e: any) {
      message.error(e?.response?.data?.detail?.message || e?.message || 'Bind failed')
    } finally {
      setBinding(false)
    }
  }

  const move = async (index: number, dir: -1 | 1) => {
    if (!capability) return
    const next = [...bindings]
    const j = index + dir
    if (j < 0 || j >= next.length) return
    ;[next[index], next[j]] = [next[j], next[index]]
    try {
      await platformReorderNodeSkillBindings(capability, next.map((b) => b.id))
      await reload()
    } catch (e: any) {
      message.error(e?.response?.data?.detail?.message || e?.message || 'Reorder failed')
    }
  }

  const columns = useMemo(
    () => [
      {
        title: t('providers.skills.columns.name'),
        dataIndex: 'display_name',
        key: 'name',
        render: (_: unknown, row: NodeSkillBindingRow) => (
          <div>
            <div style={{ fontWeight: 600 }}>{row.display_name}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.skill_key}
            </Typography.Text>
          </div>
        ),
      },
      {
        title: t('providers.skills.columns.description'),
        dataIndex: 'description',
        key: 'description',
        ellipsis: true,
        render: (v: string | null) => v || t('providers.overview.dash'),
      },
      {
        title: t('providers.skills.columns.source'),
        dataIndex: 'ownership_label',
        key: 'source',
        width: 120,
      },
      {
        title: t('providers.skills.columns.version'),
        dataIndex: 'version',
        key: 'version',
        width: 90,
        render: (v: string | null) => v || t('providers.overview.dash'),
      },
      {
        title: t('providers.skills.columns.status'),
        dataIndex: 'skill_status',
        key: 'status',
        width: 100,
        render: (v: string | null) => (
          <Tag color={v === 'PUBLISHED' ? 'green' : v === 'OFFLINE' ? 'default' : 'blue'}>{v || '—'}</Tag>
        ),
      },
      {
        title: t('providers.skills.columns.enabled'),
        key: 'enabled',
        width: 90,
        render: (_: unknown, row: NodeSkillBindingRow) => (
          <Switch
            size="small"
            checked={row.enabled}
            onChange={(checked) => {
              void (async () => {
                try {
                  if (checked) await platformEnableNodeSkillBinding(row.id)
                  else await platformDisableNodeSkillBinding(row.id)
                  await reload()
                } catch (e: any) {
                  message.error(e?.response?.data?.detail?.message || e?.message || 'Update failed')
                }
              })()
            }}
          />
        ),
      },
      {
        title: t('providers.skills.columns.default'),
        key: 'default',
        width: 110,
        render: (_: unknown, row: NodeSkillBindingRow) =>
          row.is_default ? (
            <Tag color="blue">{t('providers.overview.currentDefault')}</Tag>
          ) : (
            <Button
              type="link"
              size="small"
              disabled={!row.enabled || row.skill_status !== 'PUBLISHED'}
              onClick={() => {
                void (async () => {
                  try {
                    await platformSetNodeSkillDefault(row.id)
                    await reload()
                  } catch (e: any) {
                    message.error(e?.response?.data?.detail?.message || e?.message || 'Set default failed')
                  }
                })()
              }}
            >
              {t('providers.overview.setDefault')}
            </Button>
          ),
      },
      {
        title: t('providers.skills.columns.calls30d'),
        key: 'calls',
        width: 90,
        render: () => t('providers.overview.dash'),
      },
      {
        title: t('providers.skills.columns.actions'),
        key: 'actions',
        width: 160,
        render: (_: unknown, row: NodeSkillBindingRow, index: number) => (
          <Space size={4}>
            <Link to={`/platform/skills/${row.skill_id}`}>
              <Button type="link" size="small">
                {t('providers.skills.view')}
              </Button>
            </Link>
            <Button type="text" size="small" icon={<ArrowUpOutlined />} onClick={() => void move(index, -1)} />
            <Button type="text" size="small" icon={<ArrowDownOutlined />} onClick={() => void move(index, 1)} />
            {row.is_default ? (
              <Button
                type="link"
                size="small"
                onClick={() => {
                  void (async () => {
                    await platformClearNodeSkillDefault(row.id)
                    await reload()
                  })()
                }}
              >
                {t('providers.skills.clearDefault')}
              </Button>
            ) : null}
            <Button
              type="link"
              size="small"
              danger
              onClick={() => {
                Modal.confirm({
                  title: t('providers.skills.unbindConfirm'),
                  onOk: async () => {
                    await platformUnbindNodeSkill(row.id)
                    await reload()
                  },
                })
              }}
            >
              {t('providers.skills.unbind')}
            </Button>
          </Space>
        ),
      },
    ],
    [bindings, reload, t],
  )

  if (!capability) {
    return (
      <SectionCard title={t('providers.tabs.skills')}>
        <Empty description={t('providers.skills.selectNode')} />
      </SectionCard>
    )
  }

  return (
    <>
      <div
        data-testid="node-skills-stats"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 10,
          marginBottom: 12,
        }}
      >
        {[
          { key: 'bound', label: t('providers.skills.stats.bound'), value: stats?.bound_count ?? '—' },
          { key: 'published', label: t('providers.skills.stats.published'), value: stats?.published_count ?? '—' },
          {
            key: 'default',
            label: t('providers.skills.stats.default'),
            value: stats?.default_skill_name || t('providers.overview.dash'),
          },
          {
            key: 'calls',
            label: t('providers.skills.stats.calls30d'),
            value: stats?.calls_30d != null ? String(stats.calls_30d) : t('providers.overview.dash'),
          },
        ].map((card) => (
          <div
            key={card.key}
            style={{
              background: platformColors.surface,
              border: `1px solid ${platformColors.border}`,
              borderRadius: 10,
              padding: '12px 14px',
            }}
          >
            <div style={{ fontSize: 12, color: platformColors.secondaryText }}>{card.label}</div>
            <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>{card.value}</div>
          </div>
        ))}
      </div>

      <SectionCard
        title={t('providers.tabs.skills')}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => void reload()}>
              {t('providers.skills.refresh')}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => void openBind()} data-testid="node-skills-bind">
              {t('providers.skills.bind')}
            </Button>
          </Space>
        }
      >
        <Table
          data-testid="node-skills-table"
          rowKey="id"
          loading={loading}
          columns={columns as any}
          dataSource={bindings}
          pagination={false}
          scroll={{ x: 1100 }}
          locale={{ emptyText: t('providers.skills.empty') }}
        />
      </SectionCard>

      <Modal
        title={t('providers.skills.bindTitle')}
        open={bindOpen}
        onCancel={() => setBindOpen(false)}
        onOk={() => void onBind()}
        confirmLoading={binding}
        okButtonProps={{ disabled: !selectedSkillId }}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary">{t('providers.skills.bindHint')}</Typography.Paragraph>
        <Select
          style={{ width: '100%' }}
          showSearch
          optionFilterProp="label"
          placeholder={t('providers.skills.searchPlaceholder')}
          options={skillOptions}
          value={selectedSkillId}
          onChange={setSelectedSkillId}
          notFoundContent={
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('providers.skills.noCompatibleSkills', { capability })}
            </Typography.Text>
          }
          data-testid="node-skills-bind-select"
        />
      </Modal>
    </>
  )
}
